import { useMemo } from 'react';
import { Route, Switch, useParams } from 'react-router';

import * as graphql from '@treams/treams-network';
import { Container, Jumbo, Separator, Skeleton, Tabs, tokens } from '@treams/treams-ui';

import WorkspaceAlias from './WorkspaceAlias';
import WorkspaceBilling from './WorkspaceBilling';
import WorkspaceConnector from './WorkspaceConnector';
import WorkspaceDanger from './WorkspaceDanger';
import WorkspaceDisplayName from './WorkspaceDisplayName';
import WorkspaceEmailDomains from './WorkspaceEmailDomains';
import WorkspaceHubSpot from './WorkspaceHubSpot';
import WorkspacePlan from './WorkspacePlan';
import WorkspaceStyr from './WorkspaceStyr';
import WorkspaceSupport from './WorkspaceSupport';
import { WorkspaceAdvanced } from './types';

export default function WorkspaceDetails() {
	const { workspace: id } = useParams<{ workspace: string }>();

	const query = graphql.useWorkspacesQuery({
		variables: { filters: { prefix: id } },
		skip: !id,
	});

	const workspace: WorkspaceAdvanced | undefined = useMemo(
		() => query.data?.workspacesV2?.edges?.find(({ node }) => node.alias === id)?.node,
		[query.data, id],
	);

	const title = useMemo(() => {
		const str = workspace?.alias || id;

		return str.charAt(0).toUpperCase() + str.slice(1);
	}, [workspace, id]);

	return (
		<>
			<Jumbo
				title={title}
				backButtonLink="/settings/workspaces"
				footer={
					workspace?.connectorAdmin ? (
						<Tabs.Container>
							<Tabs.Item title="Beheer" path={`/settings/workspaces/details/${id}`} />
							<Tabs.Item title="Koppeling" path={`/settings/workspaces/details/${id}/connector`} />
						</Tabs.Container>
					) : null
				}
			/>
			{workspace && (
				<Switch>
					<Route path="/settings/workspaces/details/:workspace/connector">
						<WorkspaceConnector workspace={workspace} />
					</Route>
					<Route path="/settings/workspaces/details/:workspace">
						<Container mx="auto" mt={`${tokens.gap.page}px`} pb={`${tokens.gap.page}px`}>
							<WorkspacePlan workspace={workspace} />
							<br />
							<WorkspaceSupport workspace={workspace} />
							<br />
							<WorkspaceBilling workspace={workspace} />
							<Separator my={3} />
							<WorkspaceHubSpot workspace={workspace} />
							<WorkspaceStyr workspace={workspace} />
							<WorkspaceDisplayName workspace={workspace} />
							<WorkspaceAlias workspace={workspace} />
							<WorkspaceEmailDomains workspace={workspace} />
							<Separator my={3} />
							<WorkspaceDanger workspace={workspace} />
						</Container>
					</Route>
				</Switch>
			)}
			{!workspace && (
				<Container mx="auto" mt={`${tokens.gap.page}px`} pb={`${tokens.gap.page}px`}>
					<Skeleton.Box height={200} />
					<br />
					<Skeleton.Box height={200} />
					<br />
					<Skeleton.Box height={200} />
					<br />
					<Skeleton.Box height={200} />
				</Container>
			)}
		</>
	);
}
